import styled from "styled-components"
import { CheckCircle2, Loader2, Clock, AlertTriangle, TrendingUp, TrendingDown } from "lucide-react"
import { summarizeTasks, STATUS } from "../../utils/taskStatus"

const Strip = styled.div`
  display: grid;
  grid-template-columns: 1.4fr repeat(4, 1fr);
  gap: 12px;
  padding: 14px 18px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;

  @media (max-width: 1200px) {
    gap: 8px;
    padding: 10px 12px;
  }
  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
`

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  min-width: 0;

  @media (max-width: 1200px) {
    padding: 10px 12px;
    gap: 10px;
  }
`

const IconBubble = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${(p) => p.bg};
  color: ${(p) => p.color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: 1200px) {
    width: 36px;
    height: 36px;
    border-radius: 10px;
  }
`

const Texts = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;

  span.label {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    white-space: nowrap;
    letter-spacing: -0.01em;
  }
  span.value {
    font-size: 30px;
    font-weight: 800;
    color: ${(p) => p.color || "#0f172a"};
    line-height: 1.05;
    letter-spacing: -0.02em;
    @media (max-width: 1200px) { font-size: 22px; }
  }
  span.sub {
    font-size: 11.5px;
    font-weight: 600;
    color: #94a3b8;
    display: inline-flex;
    align-items: center;
    gap: 3px;
  }
`

const DonutWrap = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  flex-shrink: 0;

  @media (max-width: 1200px) {
    width: 52px;
    height: 52px;
  }

  .center {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    font-weight: 800;
    color: #2563eb;
    letter-spacing: -0.02em;
    @media (max-width: 1200px) { font-size: 12.5px; }
  }
`

function Donut({ value }) {
  const size = 64
  const stroke = 7
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const pct = Math.max(0, Math.min(100, value))
  const offset = c - (pct / 100) * c
  return (
    <DonutWrap>
      <svg width="100%" height="100%" viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e0e7ff" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none"
          stroke="#2563eb"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset 0.4s ease" }}
        />
      </svg>
      <div className="center">{pct}%</div>
    </DonutWrap>
  )
}

export default function StatsStrip({ tasks, referenceDate }) {
  const s = summarizeTasks(tasks, referenceDate)
  if (s.total === 0) return null

  const vsTarget = s.avgActual - s.avgBaseline
  const vsLabel = vsTarget === 0 ? "정상 진행" : (vsTarget > 0 ? `+${vsTarget}%` : `${vsTarget}%`)
  const vsColor = vsTarget < -5 ? "#ef4444" : vsTarget > 5 ? "#16a34a" : "#94a3b8"
  const VsIcon = vsTarget >= 0 ? TrendingUp : TrendingDown

  const pctOf = (n) => Math.round((n / s.total) * 100)

  return (
    <Strip>
      <Card>
        <Donut value={s.avgActual} />
        <Texts>
          <span className="label">전체 진행률</span>
          <span className="value" style={{ color: "#2563eb" }}>{s.avgActual}%</span>
          <span className="sub" style={{ color: vsColor }}>
            <VsIcon size={12} /> 예정 대비 {vsLabel}
          </span>
        </Texts>
      </Card>
      <Card>
        <IconBubble bg={STATUS.done.bg} color={STATUS.done.color}><CheckCircle2 size={22} /></IconBubble>
        <Texts color={STATUS.done.color}>
          <span className="label">완료</span>
          <span className="value">{s.done}</span>
          <span className="sub">전체 작업의 {pctOf(s.done)}%</span>
        </Texts>
      </Card>
      <Card>
        <IconBubble bg={STATUS.progress.bg} color={STATUS.progress.color}><Loader2 size={22} /></IconBubble>
        <Texts color={STATUS.progress.color}>
          <span className="label">진행 중</span>
          <span className="value">{s.progress}</span>
          <span className="sub">전체 작업의 {pctOf(s.progress)}%</span>
        </Texts>
      </Card>
      <Card>
        <IconBubble bg={STATUS.planned.bg} color={STATUS.planned.color}><Clock size={22} /></IconBubble>
        <Texts color={STATUS.planned.color}>
          <span className="label">예정</span>
          <span className="value">{s.planned}</span>
          <span className="sub">전체 작업의 {pctOf(s.planned)}%</span>
        </Texts>
      </Card>
      <Card>
        <IconBubble bg={STATUS.delayed.bg} color={STATUS.delayed.color}><AlertTriangle size={22} /></IconBubble>
        <Texts color={STATUS.delayed.color}>
          <span className="label">지연</span>
          <span className="value">{s.delayed}</span>
          <span className="sub">전체 작업의 {pctOf(s.delayed)}%</span>
        </Texts>
      </Card>
    </Strip>
  )
}
